// Random product generator for the new-project wizard's testing mode: mints
// an amusing-but-workable product concept to seed the Guided Wizard, where
// the connected V&V Assistant takes it from framing to requirements.
//
// Every roll comes from ONE concept, so the parts hang together: the name
// riffs on the gimmick, the description states it, the vision is that
// product's ambition, the problem is why today's alternatives fail the
// audience, and the audience is the one the gimmick serves. Randomness picks
// the concept and its brand name, never the pieces of a sentence.

export interface RandomProduct {
  category: string;
  name: string;
  description: string;
  vision: string;
  problem: string;
  targetUsers: string;
  /**
   * Set only on products rolled from the community pool: the shared entry's
   * server id. Its presence is what tells the card this text was written in
   * somebody else's workspace — so it offers Report rather than Share.
   */
  sharedId?: string;
  /**
   * Vote state, carried only on products from the shared pool: the all-time
   * count, the count inside the last seven days, and whether you voted. A
   * product invented in this browser and not yet published has nothing to
   * vote on, so these stay undefined for it.
   */
  votes?: number;
  votesWeek?: number;
  voted?: boolean;
}

interface Concept {
  category: string;
  /** Brandable names that all riff on this concept's gimmick. */
  names: string[];
  /** What the product is — a noun phrase that follows "A ". */
  what: string;
  /** What it does — a verb phrase that follows "that ". */
  does: string;
  /**
   * Who it is for — a noun phrase that follows "for ". Write the people this
   * exact gimmick serves, caught in the specific moment it fixes ("engineers
   * who reconstruct yesterday from browser history thirty seconds before
   * stand-up"), never a generic demographic that would fit any product.
   */
  audience: string;
  /** Why today's alternatives fail that audience. A complete sentence. */
  gap: string;
  /** The ambition — a noun phrase that follows "becomes ". */
  ambition: string;
}

const CONCEPTS: Concept[] = [
  {
    category: 'software',
    names: ['Yesterdaily', 'Standupp', 'Recapper'],
    what: 'stand-up meeting assistant',
    does: 'writes your daily update from your actual commit history and gently flags the days you did nothing',
    audience: 'engineers who reconstruct yesterday from browser history thirty seconds before stand-up',
    gap: 'Stand-up updates are reconstructed from memory at 9:01 a.m., which is why they are equal parts fiction and apology.',
    ambition: 'the two-minute ritual that makes stand-up honest without making it longer',
  },
  {
    category: 'software',
    names: ['Bailout', 'Exit Strategy', 'Phoney'],
    what: 'calendar assistant',
    does: 'invents a plausible, escalating excuse and calls you out of any meeting that runs twenty minutes over',
    audience: 'people whose 3pm quick sync has run twenty minutes over every week since March',
    gap: 'Leaving a meeting early takes either courage or a believable emergency, and almost nobody has one on hand at 4pm.',
    ambition: 'the escape hatch every over-booked calendar quietly needs',
  },
  {
    category: 'hardware',
    names: ['Fogoff', 'DeskCannon', 'Smokescreen'],
    what: 'desk-mounted fog machine and status lamp',
    does: 'releases a small dramatic fog cloud when your focus timer starts and glows green when you are interruptible',
    audience: 'open-plan developers whose deep work ends with a fourth cheerful "got a sec?" before lunch',
    gap: 'Headphones are ambiguous, do-not-disturb statuses are invisible from three desks away, and saying "I am busy" out loud all day is exhausting.',
    ambition: 'the availability signal readable from across the room, with theatre',
  },
  {
    category: 'hardware',
    names: ['Truce', 'Degree Zero', 'ThermoPeace'],
    what: 'thermostat with a voting panel',
    does: 'requires a quorum before anyone can change the temperature and keeps a log of who keeps trying',
    audience: 'households where one person wears a fleece indoors and another opens windows in February',
    gap: 'A thermostat has one setting and many stakeholders, so the coldest-blooded person wins by stealth every single afternoon.',
    ambition: 'the end of the thermostat cold war, complete with an audit trail',
  },
  {
    category: 'video game',
    names: ['Krakenpark', 'Leviathan Valet', 'Deep Reverse'],
    what: 'physics puzzle game',
    does: 'has you parallel-park increasingly enormous sea creatures into increasingly small harbours',
    audience: 'players who bounced off world-saving epics and want to be quietly excellent at reversing a blue whale into a fishing berth',
    gap: 'The store is full of games about saving the world and nearly empty of games about doing a small, strange task perfectly.',
    ambition: 'the game people sink sixty hours into and describe to friends as "you park a whale"',
  },
  {
    category: 'video game',
    names: ['Afterclerk', 'Spectral Services', 'HauntDesk'],
    what: 'cozy management game',
    does: 'puts you behind the counter of a permit office for ghosts who are extremely particular about paperwork',
    audience: 'people who unwind by sorting other people’s paperwork and would prefer the customers to be haunted',
    gap: 'Management games simulate empires; nobody serves the player who just wants a tidy desk and a satisfied customer.',
    ambition: 'the comfort game that makes admin work feel like a warm bath',
  },
  {
    category: 'toy',
    names: ['Mimicorn', 'EchoPal', 'Parrotplush'],
    what: 'plush axolotl with a hidden microphone',
    does: 'repeats the last thing it heard in a tiny, slightly wrong voice',
    audience: 'parents of six-year-olds who have just discovered that repeating everything back is comedy',
    gap: 'Talking toys cycle through the same forty phrases until the batteries die, and children find the pattern in an afternoon.',
    ambition: 'the toy that gets confiscated at bedtime and smuggled back by breakfast',
  },
  {
    category: 'toy',
    names: ['Sulk', 'Broodcloud', 'Moodlump'],
    what: 'plush storm cloud',
    does: 'glows brighter and rumbles louder the longer it is left abandoned on the floor',
    audience: 'families who step over the same three abandoned toys nightly on the way to the tidy-up argument',
    gap: 'Tidying up is a nightly argument because toys have no opinion about being left underfoot.',
    ambition: 'the toy that does the nagging so nobody in the house has to',
  },
  {
    category: 'kitchen appliance',
    names: ['Toastmood', 'Browning Point', 'Charmometer'],
    what: 'countertop toaster',
    does: 'asks how your morning is going and browns the bread to match, from "pale and hopeful" to "carbonised"',
    audience: 'people who set the dial to 4, get charcoal, and start every day negotiating with an appliance',
    gap: 'Toasters offer a numbered dial that means nothing, so every slice is a gamble placed before anyone is awake enough to gamble.',
    ambition: 'the appliance that finally admits toast is an emotional decision',
  },
  {
    category: 'kitchen appliance',
    names: ['Kevinproof', 'Beanwatch', 'Brewdential'],
    what: 'office coffee grinder with a fingerprint lock and a bean ledger',
    does: 'grinds only your beans, weighs every gram taken, and posts a weekly leaderboard of who took whose',
    audience: 'coffee-obsessed office workers locked in a passive-aggressive hunt for the perfect brew and for whoever keeps taking their beans',
    gap: 'The office kitchen runs on an honour system that collapses the moment someone brings in a good single-origin bag, and there is never any evidence — only suspicion and an empty jar.',
    ambition: 'the appliance that settles the office bean wars with data instead of passive-aggressive sticky notes',
  },
  {
    category: 'wearable',
    names: ['Sighence', 'Huffwatch', 'Exhale'],
    what: 'lapel pin',
    does: 'counts your sighs per hour and charts them against your calendar',
    audience: 'knowledge workers who suspect the recurring Thursday 2pm is why they feel like this, and want receipts',
    gap: 'Wearables obsess over steps and sleep while ignoring the vital signs of office survival.',
    ambition: 'the wearable that turns a vaguely terrible week into a chart you can show someone',
  },
  {
    category: 'pet tech',
    names: ['Petition', 'Meowdio', 'Grievance Bell'],
    what: 'paw-operated intercom',
    does: 'lets pets file formal complaints, which arrive as time-stamped voice notes on your phone',
    audience: 'cat owners who already answer out loud when the cat stares at a full bowl and screams',
    gap: 'Pets currently communicate by knocking things off tables — an unstructured feedback channel with no audit trail.',
    ambition: 'the official grievance procedure every household pet has been demanding for centuries',
  },
  {
    category: 'board game',
    names: ['Broth Runners', 'Souperior', 'Ladle & Order'],
    what: 'social deduction board game',
    does: 'has players smuggling soup across a fantasy border while one of them is secretly the customs inspector',
    audience: 'game groups who can recite each other’s tells after six years of the same three deduction games',
    gap: 'Every group has played the same three deduction games to death and now knows exactly how each other lies.',
    ambition: 'the game night that ends in laughter, betrayal, and one strongly worded house rule',
  },
  {
    category: 'garden tech',
    names: ['Gnomecast', 'Squirrelspotter', 'Lawn Commentary'],
    what: 'solar-powered garden gnome',
    does: 'live-narrates squirrel activity in the voice of an increasingly invested sports commentator',
    audience: 'gardeners who have named the squirrel that keeps defeating their bird-feeder engineering',
    gap: 'Garden pests get treated as a problem to be solved when they are, in fact, the best entertainment on the street.',
    ambition: 'the garden ornament people leave the kitchen window open for',
  },
  {
    category: 'robot',
    names: ['Crustodian', 'Slicekeeper', 'Leftover Watch'],
    what: 'palm-sized fridge robot',
    does: 'guards the last slice of pizza and reports, with photographic evidence, exactly who took it',
    audience: 'flatshares where the container says DO NOT EAT, in marker, and it gets eaten anyway',
    gap: 'A name written on a container is only a suggestion: the shared fridge is a lawless place with no witnesses.',
    ambition: 'the tiny robot that finally brings law to the shared fridge',
  },
];

// Vision patterns. Each reads correctly with an ambition phrased as a noun
// phrase, so the sentence stays coherent whichever concept is rolled.
const VISION_PATTERNS: ((name: string, ambition: string) => string)[] = [
  (name, ambition) => `${name} becomes ${ambition}.`,
  (name, ambition) => `Make ${name} ${ambition}.`,
];

const pick = <T>(items: T[]): T => items[Math.floor(Math.random() * items.length)];

const fromConcept = (concept: Concept): RandomProduct => {
  const name = pick(concept.names);
  return {
    category: concept.category,
    name,
    description: `A ${concept.what} that ${concept.does}.`,
    vision: pick(VISION_PATTERNS)(name, concept.ambition),
    problem: concept.gap,
    targetUsers: concept.audience,
  };
};

/**
 * Roll a product from `extra` — the shared pool plus the agent-invented
 * products kept by `loadInventedProducts` — so a good invention keeps coming
 * back on later rerolls instead of being seen once.
 *
 * `includeBuiltins` mixes in the concepts above, and the caller should pass it
 * only when `extra` is empty. The same concepts are seeded into the shared
 * pool server-side (internal/seeds/product_concepts.go), so on a working
 * deployment they arrive as real rows that can be voted for; rendering them
 * here as well would put a second, unvotable copy of the same product in the
 * roll. They remain the fallback for the one case the pool cannot cover — an
 * API that will not answer — where a duller card beats a blank one.
 */
export const generateRandomProduct = (
  extra: RandomProduct[] = [],
  includeBuiltins = true
): RandomProduct => {
  const builtins = includeBuiltins ? CONCEPTS.length : 0;
  const index = Math.floor(Math.random() * (builtins + extra.length));
  return index < builtins ? fromConcept(CONCEPTS[index]) : { ...extra[index - builtins] };
};

// --------------------------------------------------------------------------
// Agent-invented products
// --------------------------------------------------------------------------
// The built-in concepts above are a fixed bucket, so they go stale as soon as
// you have seen them all. With a runner connected, the same brief that shaped
// those concepts is handed to the member's own agent, which invents something
// nobody has read before — including categories this file never imagined.

/**
 * The brief given to the agent. `avoid` carries concepts already shown in this
 * session so a fresh click keeps producing something new.
 */
/**
 * A worked example, rendered from a real built-in concept so the brief always
 * shows the standard it is asking for rather than describing it. Two of these
 * do more for quality than any amount of adjectives about "dry humour".
 */
const workedExample = (concept: Concept): string =>
  JSON.stringify(fromConcept(concept), null, 2);

/** The fields every usable product must carry, in card order. */
const REQUIRED_FIELDS: (keyof RandomProduct)[] = [
  'category',
  'name',
  'description',
  'vision',
  'problem',
  'targetUsers',
];

/**
 * The brief given to the agent.
 *
 * The built-in concepts are funny by construction: the name is written to
 * riff on the gimmick, the vision is generated from the name, and the
 * audience is a moment rather than a demographic. An agent gets none of that
 * for free, which is why early inventions came back with names bolted onto
 * unrelated products ("QuibbleMax", "DoomX"). So the brief states each of
 * those invariants as a rule, shows two real examples, and ends with a
 * self-check; `describeProductFlaws` then enforces the same rules on the way
 * back in.
 *
 * `avoid` carries concepts already shown this session; `rejected` carries the
 * faults in a previous attempt, so a retry is told what to fix.
 */
export const inventProductPrompt = (avoid: string[], rejected: string[] = []): string => {
  const avoidLine = avoid.length
    ? `\nAlready used in this session — invent something different from all of these: ${avoid.join('; ')}.\n`
    : '';
  const rejectedLine = rejected.length
    ? `\nYour previous attempt was rejected. Fix all of this: ${rejected.join(' ')}\n`
    : '';
  // Two different concepts, so the examples never look like one template.
  const first = CONCEPTS[Math.floor(Math.random() * CONCEPTS.length)];
  const rest = CONCEPTS.filter((c) => c !== first);
  const second = rest[Math.floor(Math.random() * rest.length)];

  return `Invent ONE fictional product concept to seed a requirements-tool demo.

The humour is specific and deadpan: take one small, true, widely-recognised frustration and treat it with complete engineering seriousness. It is never wordplay salad, never a random mash-up of two nouns, and never zany for its own sake. The product must still be something an engineer could write real requirements for.

Here are two examples at exactly the standard required:

${workedExample(first)}

${workedExample(second)}

Notice what makes those work, and copy it:
- The NAME gives the joke away. Read the name and the category alone and you can half-guess the gimmick. Never bolt a generic suffix onto an unrelated word — no "Max", "Pro", "X", "Plus", "Hub", "AI", "360", "Ultra", "-ify", "-ly". A name that would fit any product in any category is the single most common failure here.
- The DESCRIPTION names one concrete mechanism, not a category ("releases a small dramatic fog cloud when your focus timer starts", not "improves focus in the workplace").
- The AUDIENCE is a moment, with a specific detail in it — a count, a time of day, a recurring irritant with a name — not a demographic. "engineers" is wrong; "engineers who reconstruct yesterday from browser history thirty seconds before stand-up" is right.
- The PROBLEM says why the things people already do fail these particular people. It never mentions the product.
- The VISION starts with the product name and states the ambition for THIS product.

Pick any category you like: software, hardware, a video game, a toy, a kitchen appliance, a wearable, pet tech, a board game, garden kit, a robot, sports equipment, transport, stationery, musical instruments — surprise me. Do not copy the examples' categories.
${avoidLine}${rejectedLine}
Field rules — every field must describe the SAME product:
- "category": a few words, lowercase (e.g. "kitchen appliance").
- "name": one or two words, riffing on this product's own gimmick.
- "description": exactly one sentence of the form "A <what it is> that <what it does>." — the concrete gimmick.
- "vision": one sentence that CONTAINS the product name and states the ambition for it.
- "problem": one or two sentences on why today's alternatives fail these particular people. Never mentions the product.
- "targetUsers": at least eight words, a clause describing the people this exact gimmick serves, caught in the moment it fixes.

Before you answer, check your own draft: does the name give away the gimmick? Does the vision contain the name? Is the audience a moment rather than a label? Would someone who has lived that frustration laugh in recognition? If any answer is no, rewrite it.

Reply with ONLY a JSON object, no commentary and no code fence:
{"category":"","name":"","description":"","vision":"","problem":"","targetUsers":""}`;
};

export const parseInventedProduct = (text: string): RandomProduct | null => {
  if (!text) return null;
  const start = text.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (escaped) {
      escaped = false;
    } else if (ch === '\\') {
      escaped = true;
    } else if (ch === '"') {
      inString = !inString;
    } else if (!inString && ch === '{') {
      depth += 1;
    } else if (!inString && ch === '}') {
      depth -= 1;
      if (depth === 0) {
        let parsed: any;
        try {
          parsed = JSON.parse(text.slice(start, i + 1));
        } catch {
          return null;
        }
        if (!parsed || typeof parsed !== 'object') return null;
        const product: Record<string, string> = {};
        for (const field of REQUIRED_FIELDS) {
          const value = parsed[field];
          if (typeof value !== 'string' || !value.trim()) return null;
          product[field] = value.trim();
        }
        return product as unknown as RandomProduct;
      }
    }
  }
  return null;
};

// --------------------------------------------------------------------------
// Keeping invented products
// --------------------------------------------------------------------------
// Inventions are worth keeping: an agent-written concept is usually better
// than anything hardcoded here, and re-inventing costs a run. They are stored
// in this browser (localStorage) and joined to the reroll pool, so a good one
// comes back for free. Storage is per browser and never leaves the machine;
// clearing site data clears them.

const INVENTED_STORAGE_KEY = 'openv-invented-products';
/** Kept newest-first; older inventions fall off the end. */
const INVENTED_LIMIT = 50;

const isProduct = (value: any): value is RandomProduct =>
  !!value &&
  typeof value === 'object' &&
  REQUIRED_FIELDS.every((field) => typeof value[field] === 'string' && value[field].trim());

/** Invented products kept in this browser, newest first. */
export const loadInventedProducts = (): RandomProduct[] => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(INVENTED_STORAGE_KEY);
  } catch {
    return []; // private mode or blocked storage — the built-ins still work
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const products = Array.isArray(parsed) ? parsed : parsed?.products;
    if (!Array.isArray(products)) return [];
    return products.filter(isProduct).slice(0, INVENTED_LIMIT);
  } catch {
    return [];
  }
};

/**
 * Keep one invented product, returning the new list. Re-inventing something
 * with the same name replaces the older copy rather than stacking duplicates.
 */
export const saveInventedProduct = (product: RandomProduct): RandomProduct[] => {
  if (!isProduct(product)) return loadInventedProducts();
  const next = [
    product,
    ...loadInventedProducts().filter(
      (p) => p.name.toLowerCase() !== product.name.toLowerCase()
    ),
  ].slice(0, INVENTED_LIMIT);
  try {
    localStorage.setItem(INVENTED_STORAGE_KEY, JSON.stringify({ v: 1, products: next }));
  } catch {
    // Storage full or blocked: the product still shows for this session.
  }
  return next;
};

/** Forget every kept invention (the built-in concepts are unaffected). */
export const clearInventedProducts = (): void => {
  try {
    localStorage.removeItem(INVENTED_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
};

/** Whether a rolled product came from the kept inventions. */
export const isInventedProduct = (product: RandomProduct, invented: RandomProduct[]): boolean =>
  invented.some((p) => p.name === product.name && p.description === product.description);

/**
 * Faults that make an invented product read worse than a built-in one, in the
 * words the retry prompt will quote back. Empty means it holds together.
 *
 * These are exactly the invariants the built-in concepts satisfy by
 * construction — the name riffs on the gimmick, the vision carries the name,
 * the description is one mechanism, the audience is a moment — so an
 * invention that fails one is visibly weaker than the pool it would join.
 */
export const describeProductFlaws = (product: RandomProduct): string[] => {
  const flaws: string[] = [];
  const name = product.name.trim();

  // A word with a generic suffix bolted on fits any product in any category,
  // which is the failure that produced "QuibbleMax" and "DoomX".
  if (/(max|pro|plus|hub|360|ultra)$/i.test(name)) {
    flaws.push(`The name "${name}" is a word with a generic suffix bolted on; name it after its own gimmick instead.`);
  }
  if (name.split(/\s+/).length > 3) {
    flaws.push(`The name "${name}" is a sentence, not a brand.`);
  }
  if (!product.vision.includes(name)) {
    flaws.push('The vision must contain the product name.');
  }
  if (!/^A .+ that .+\.$/.test(product.description.trim())) {
    flaws.push('The description must be one sentence of the form "A <what it is> that <what it does>."');
  }
  if (product.targetUsers.trim().split(/\s+/).length < 8) {
    flaws.push('The audience is a label, not a moment: describe the people this gimmick serves, caught in the situation it fixes.');
  }
  if (!/\b(who|whom|whose|whoever|where|which|that)\b/i.test(product.targetUsers)) {
    flaws.push('The audience needs a qualifying clause ("… who still …"), not a bare demographic.');
  }
  if (product.problem.toLowerCase().includes(name.toLowerCase())) {
    flaws.push("The problem statement must not mention the product — it is why today's alternatives fail.");
  }
  return flaws;
};

// --------------------------------------------------------------------------
// Community-shared products
// --------------------------------------------------------------------------
// Products other people chose to share (POST /api/v1/shared-products) are
// readable by every workspace, so the pool keeps growing without anyone
// spending a run. The server sanitizes them to inert single-line text and
// serves no author identity; the client's job is only to keep track of which
// rolls came from there, so a shared card can be reported rather than shared
// back.

/** The API shape of a shared product (snake_case, plus server fields). */
export interface SharedProductPayload {
  id: string;
  category: string;
  name: string;
  description: string;
  vision: string;
  problem: string;
  target_users: string;
  /** All-time votes, votes inside the last seven days, and your own vote. */
  votes?: number;
  votes_week?: number;
  voted?: boolean;
}

/**
 * Convert a shared-pool entry into a rollable product.
 *
 * Vote fields default to zero/false rather than undefined so the card can
 * render a count without a server that predates voting making it blank.
 */
export const fromSharedProduct = (payload: SharedProductPayload): RandomProduct => ({
  category: payload.category,
  name: payload.name,
  description: payload.description,
  vision: payload.vision,
  problem: payload.problem,
  targetUsers: payload.target_users,
  sharedId: payload.id,
  votes: payload.votes || 0,
  votesWeek: payload.votes_week || 0,
  voted: !!payload.voted,
});

/** Whether a rolled product came from the community pool. */
export const isSharedProduct = (product: RandomProduct): boolean => !!product.sharedId;

/** The payload sent when sharing a product with every workspace. */
export const toSharePayload = (product: RandomProduct) => ({
  category: product.category,
  name: product.name,
  description: product.description,
  vision: product.vision,
  problem: product.problem,
  target_users: product.targetUsers,
});
